<script lang="ts" setup>
import { PropType, computed, useSlots } from 'vue'
import type { RouteLocationRaw } from 'vue-router'
import type { BreadcrumbItem } from 'vuetify/lib/components/VBreadcrumbs/VBreadcrumbs.mjs'

import BackButton from '@/components/BackButton.vue'

const slots = useSlots()

const skeletonPresets = [
  'table',
  'article',
  'list-item@5',
  'image, text',
] as const

type SkeletonPreset = (typeof skeletonPresets)[number]

const props = defineProps({
  title: { type: String, required: false, default: null },
  mobileFab: { type: Boolean, default: false },
  isMobile: { type: Boolean, default: false },
  onlyWhite: { type: Boolean, default: false },
  progress: { type: Number, default: null },
  items: { type: Array as PropType<BreadcrumbItem[]>, default: () => [] },
  backButtonTo: { type: Object as PropType<RouteLocationRaw>, default: null },
  bgWhite: { type: Boolean, default: false },
  contextualNav: { type: Boolean, default: false },
  mobileFooter: { type: Boolean, default: false },
  color: { type: String, default: null, required: false },
  publicMode: { type: Boolean, default: false },
  loading: { type: Boolean, default: false },
  isMembers: { type: Boolean, default: false, required: false },
  skeletonType: {
    type: String as PropType<SkeletonPreset | string>,
    default: 'table',
  },
  footerReserveHeight: { type: Number, default: 96 },
  titleColor: { type: String, default: 'text-green' },
  titleWeight: { type: String, default: 'font-weight-bold' },
  titleSize: { type: String, default: 'text-h6' },
  subheaderFullBleed: { type: Boolean, default: false },
  header: { type: Boolean, default: true },
  constrainWidth: { type: Boolean, default: true },
  maxWidth: {
    type: [Number, String] as PropType<number | string>,
    default: 1200,
  },
  contentTopPadding: {
    type: String as PropType<'auto' | 'none' | 'subheader' | 'default'>,
    default: 'auto',
  },
  import: { type: Boolean, default: false, required: false },
})

const contentBgClass = computed(() =>
  props.bgWhite || props.onlyWhite || props.isMobile
    ? 'bg-white'
    : 'bg-grey-lighten-5'
)

const headerColor = computed(() => {
  if (props.color) return 'transparent'
  if (props.bgWhite) return 'white'
  return !props.isMobile ? 'grey-lighten-5' : 'white'
})

const topPadStyle = computed((): Record<string, string> => {
  if (props.isMobile) return { paddingTop: '0px' }
  if (props.import === true) return {}

  if (props.contentTopPadding === 'none') return { paddingTop: '0px' }
  if (props.contentTopPadding === 'subheader') return { paddingTop: '64px' }
  if (props.contentTopPadding === 'default') return { paddingTop: '24px' }

  if (slots.subheader) return { paddingTop: '72px' }

  return { paddingTop: '24px' }
})

const normalizeMaxWidth = (value: number | string): string => {
  if (typeof value === 'number') return `${value}px`

  const trimmed = value.trim()

  if (/^\d+$/.test(trimmed)) return `${trimmed}px`

  return trimmed
}

const resolvedMaxWidth = computed(() =>
  props.constrainWidth ? normalizeMaxWidth(props.maxWidth) : 'none'
)

const contentWrapStyle = computed<Record<string, string>>(() => ({
  maxWidth: resolvedMaxWidth.value,
}))

const contentWrapClass = computed(() =>
  props.constrainWidth ? 'mx-md-auto' : ''
)

const subheaderInnerStyle = computed<Record<string, string>>(() => ({
  maxWidth: props.subheaderFullBleed ? 'none' : resolvedMaxWidth.value,
}))

const showMobileTitlebar = computed(
  () => props.contextualNav && props.isMobile && !!props.title && props.header
)
</script>

<template>
  <v-app>
    <v-main
      class="d-flex flex-column"
      :class="contentBgClass"
      :style="{
        marginTop: props.contextualNav ? '64px' : '',
      }"
    >
      <v-sheet
        v-if="props.header && !(props.contextualNav && props.isMobile)"
        class="d-flex align-center position-relative"
        :class="props.contextualNav ? 'border-b-thin bg-white' : ''"
        :color="headerColor"
        :height="48"
      >
        <div v-if="props.color" class="header-color-abs">
          <slot name="header-color" />
        </div>

        <div
          class="w-100 h-100 d-flex align-center justify-space-between header-content px-4"
        >
          <div class="d-flex align-center min-w-0 flex-1">
            <template v-if="props.title || props.isMobile">
              <BackButton
                v-if="
                  !props.publicMode && (props.isMobile || props.backButtonTo)
                "
                :to="props.backButtonTo"
              />

              <h2
                v-if="props.isMobile"
                class="title-ellipsis text-subtitle-1 m-0 ml-2"
              >
                {{ props.title }}
              </h2>

              <div
                v-else-if="props.title"
                class="d-flex align-center ga-3 ml-6"
              >
                <span
                  :class="[
                    props.titleColor,
                    props.titleWeight,
                    props.titleSize,
                  ]"
                >
                  {{ props.title }}
                </span>

                <slot name="header-right" />
              </div>
            </template>

            <template v-else-if="props.items.length && !props.isMobile">
              <v-breadcrumbs
                :items="props.items"
                divider=">"
                class="eve-breadcrumbs text-grey-darken-1"
              >
                <template #title="{ item, index }">
                  <span
                    :class="
                      index === props.items.length - 1
                        ? 'green-breadcrumbs'
                        : 'text-grey-darken-1'
                    "
                  >
                    {{ item.title }}
                  </span>
                </template>
              </v-breadcrumbs>
            </template>
          </div>

          <div class="d-flex align-center ml-2">
            <div v-if="props.progress !== null" class="align-center w-100">
              <span class="font-weight-bold text-subtitle-1 mr-2">
                {{ props.progress }}%
              </span>

              <span class="text-caption text-grey-darken-2 mr-6">
                {{
                  $t(
                    props.progress === 100
                      ? 'events.status.ready_to_publish'
                      : 'events.status.draft'
                  )
                }}
              </span>
            </div>

            <slot name="publish-button" />
          </div>
        </div>
      </v-sheet>

      <v-sheet
        v-if="showMobileTitlebar"
        class="mobile-titlebar"
        color="white"
        height="48"
      >
        <div class="mobile-titlebar__inner">
          <BackButton
            v-if="!props.publicMode && (props.isMobile || props.backButtonTo)"
            :to="props.backButtonTo"
            class="mobile-titlebar__back"
          />

          <h2 class="mobile-titlebar__title">
            {{ props.title }}
          </h2>

          <div class="mobile-titlebar__right">
            <slot name="header-right" />
            <slot name="publish-button" />
          </div>
        </div>
      </v-sheet>

      <div
        v-if="$slots.subheader"
        class="eve-subheader-wrap"
        :class="[
          { 'eve-subheader-full': props.subheaderFullBleed },
          { 'eve-subheader-mobile': props.isMobile },
          { 'eve-subheader-after-mobile-titlebar': showMobileTitlebar },
        ]"
      >
        <div
          class="eve-subheader-inner"
          :class="
            props.subheaderFullBleed
              ? 'px-0'
              : props.isMobile
                ? 'px-4'
                : 'px-md-4'
          "
          :style="subheaderInnerStyle"
        >
          <slot name="subheader" />
        </div>
      </div>

      <div v-if="props.progress !== null" class="w-100">
        <v-progress-linear
          :model-value="props.progress"
          color="#8DC63F"
          height="3"
          background-color="#F1F5ED"
        />
      </div>

      <v-container
        fluid
        class="d-flex flex-column flex-grow-1 px-0 pb-0"
        :class="[contentBgClass]"
        :style="topPadStyle"
      >
        <div
          class="w-100 flex-grow"
          :class="[contentWrapClass, props.isMobile ? 'pa-0' : 'px-md-4']"
          :style="contentWrapStyle"
        >
          <transition name="fade-fast" mode="out-in">
            <div
              :key="props.loading ? 'skeleton' : 'content'"
              :role="props.loading ? 'status' : undefined"
              :aria-busy="props.loading ? 'true' : 'false'"
              aria-live="polite"
            >
              <template v-if="props.loading">
                <slot name="skeleton" :type="props.skeletonType">
                  <v-skeleton-loader
                    :type="props.skeletonType"
                    class="rounded-lg"
                    boilerplate
                  />
                </slot>
              </template>

              <template v-else>
                <slot />
              </template>
            </div>
          </transition>
        </div>

        <div
          v-if="
            $slots['footer-actions'] && (!props.isMobile || props.mobileFooter)
          "
          :style="{ height: `${props.footerReserveHeight}px` }"
        />

        <v-sheet
          v-if="
            $slots['footer-actions'] && (!props.isMobile || props.mobileFooter)
          "
          class="footer-actions-wrapper"
          :class="{
            'footer-actions-mobile': props.isMobile && props.mobileFooter,
          }"
        >
          <div
            class="footer-actions-inner"
            :class="contentWrapClass"
            :style="contentWrapStyle"
          >
            <slot name="footer-actions" />
          </div>
        </v-sheet>

        <v-btn
          v-if="props.mobileFab && props.isMobile"
          icon="mdi-plus"
          color="primary"
          class="position-fixed z-2 elevation-2 bottom-14 right-10"
          aria-label="Criar"
        />
      </v-container>
    </v-main>
  </v-app>
</template>

<style scoped>
:deep(.v-sheet) {
  overflow: visible;
}

.header-color-abs {
  position: absolute;
  inset: 0 auto auto 0;
  width: 100%;
  height: 100%;
  z-index: 0;
}

.header-content {
  position: relative;
  z-index: 1;
}

.title-ellipsis {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  max-width: 72vw;
}

.mobile-titlebar {
  background: #fff;
  border-bottom: 1px solid #e0e0e0;
  z-index: 5;
}

.mobile-titlebar__inner {
  height: 48px;
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 0 16px 0 0;
}

.mobile-titlebar__title {
  margin: 0;
  font-size: 18px;
  font-weight: 400;
  color: rgba(0, 0, 0, 0.6);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  flex: 1;
}

.mobile-titlebar__right {
  display: flex;
  align-items: center;
}

.footer-actions-wrapper {
  position: fixed;
  bottom: 0;
  left: var(--expert-sidebar-width, 0px);
  right: 0;
  width: auto;
  background: white;
  border-top: 1px solid #e0e0e0;
  padding: 16px;
  z-index: 100;
  transition: left 0.2s ease;
}

.footer-actions-inner {
  width: 100%;
  min-width: 0;
  margin: 0 auto;
  display: flex;
  justify-content: center;
}

.footer-actions-mobile {
  display: flex;
  padding-top: 24px;
  padding-bottom: 24px;
}

.flex-1 {
  flex: 1 1 auto;
}

.min-w-0 {
  min-width: 0;
}

.fade-fast-enter-active,
.fade-fast-leave-active {
  transition: opacity 0.12s ease;
}

.fade-fast-enter-from,
.fade-fast-leave-to {
  opacity: 0;
}

.eve-subheader-wrap {
  width: 100%;
  height: 48px;
  margin-top: 14px;
  background: white;
  border-bottom: 1px solid #e5e7eb;
  display: flex;
}

.eve-subheader-inner {
  width: 100%;
  min-width: 0;
  margin: 0;
  justify-content: flex-start;
}

.eve-subheader-full .eve-subheader-inner {
  max-width: none;
}

.eve-subheader-mobile {
  height: auto;
  flex-direction: column;
}

.eve-subheader-mobile .eve-subheader-inner {
  display: flex;
  flex-direction: column;
}

.eve-subheader-after-mobile-titlebar {
  margin-top: 0 !important;
}

.eve-breadcrumbs .v-breadcrumbs-item--disabled {
  opacity: 1 !important;
}

.eve-breadcrumbs .green-breadcrumbs {
  opacity: 1 !important;
  color: var(--standart-theme---EVE-primary, #8dc63f) !important;
}

@media (max-width: 600px) {
  .eve-subheader-wrap {
    border-radius: 0 !important;
  }

  :deep(.v-main) {
    padding-top: var(--v-layout-top, 0px) !important;
    padding-right: 0 !important;
    padding-bottom: 0 !important;
    padding-left: 0 !important;
  }

  :deep(.v-application),
  :deep(.v-application__wrap),
  :deep(.v-main) {
    background: #fff !important;
  }
}
</style>
